/**
 * Pure builds per version on registry.pure.dev (moving-pointer scheme,
 * 2026-10-12 — internal/registry/backend/builds.ts, plan
 * internal/compiler/plans/2026-10-12-moving-version-pointer.md):
 *
 *   - every build of upstream `pkg@X.Y.Z` is its own immutable, pinnable
 *     version `X.Y.Z-pure.<compiler>[.<n>]`;
 *   - `X.Y.Z` is a moving pointer to the newest live build (or upstream's
 *     original when none / rolled back).
 *
 * Read from the registry's PUBLIC read-only route `GET /-/builds/<pkg>`
 * (CORS-open; everything in it is public in the packument anyway). Pure
 * helpers here, unit-tested by registryBuilds.test.ts; the panel is
 * components/registry/VersionBuilds.tsx.
 */

export interface RegistryBuild {
  version: string;
  compilerVersion: string;
  publishedAt: string;
  integrity: string;
  tarball: string;
  pointerIntegrity: string | null;
  pointerTarball: string | null;
  withdrawn: { at: string; reason: string } | null;
}

export interface RegistryVersionBuilds {
  /** The build X.Y.Z points at; null = upstream's original (or nothing). */
  current: string | null;
  /** An operator rolled X.Y.Z back to upstream. */
  rolledBack: { at: string; reason: string } | null;
  /** Newest first. */
  builds: RegistryBuild[];
}

export interface RegistryBuildsView {
  name: string;
  versions: Record<string, RegistryVersionBuilds>;
}

/** registry.<domain> for a management.<domain> page; registry.pure.dev
 *  otherwise (local dev). */
export function registryPublicUrl(hostname: string): string {
  const m = /^management\.(.+)$/.exec(hostname);
  return m ? `https://registry.${m[1]}` : "https://registry.pure.dev";
}

export function buildsUrl(registryUrl: string, pkg: string): string {
  const name = pkg.startsWith("@")
    ? `${pkg.split("/")[0]}/${encodeURIComponent(pkg.split("/")[1] ?? "")}`
    : encodeURIComponent(pkg);
  return `${registryUrl.replace(/\/$/, "")}/-/builds/${name}`;
}

export type BuildRole = "current" | "available" | "withdrawn" | "no-pointer";

/** What one build is for X.Y.Z right now. */
export function buildRole(
  v: RegistryVersionBuilds,
  b: RegistryBuild,
): BuildRole {
  if (b.withdrawn) return "withdrawn";
  if (v.current === b.version) return "current";
  return b.pointerIntegrity ? "available" : "no-pointer";
}

export function buildRoleLabel(role: BuildRole, version: string): string {
  switch (role) {
    case "current":
      return `${version} points here`;
    case "available":
      return "pinnable";
    case "withdrawn":
      return "withdrawn (still installable when pinned)";
    case "no-pointer":
      return "pinnable only";
  }
}

/** One line saying what `pkg@version` resolves to. */
export function pointerSummary(
  version: string,
  v: RegistryVersionBuilds | undefined,
): string {
  if (!v || v.builds.length === 0) return `${version} → upstream's original`;
  if (v.current) return `${version} → ${v.current}`;
  if (v.rolledBack)
    return `${version} → upstream's original (rolled back: ${v.rolledBack.reason})`;
  return `${version} → upstream's original (no live build)`;
}

/** The exact-pin install command for a build. */
export function pinCommand(pkg: string, buildVersion: string): string {
  return `npm i -E ${pkg}@${buildVersion}`;
}

/** sha512-AAAA…ZZZZ for display. */
export function shortIntegrity(i: string | null): string {
  if (!i) return "—";
  const [alg, b64] = i.split("-", 2);
  return b64 && b64.length > 16
    ? `${alg}-${b64.slice(0, 8)}…${b64.slice(-6)}`
    : i;
}

export async function fetchRegistryBuilds(
  registryUrl: string,
  pkg: string,
  fetchImpl: typeof fetch = fetch,
): Promise<RegistryBuildsView> {
  const res = await fetchImpl(buildsUrl(registryUrl, pkg), {
    headers: { accept: "application/json" },
  });
  if (!res.ok)
    throw new Error(`registry builds for ${pkg}: HTTP ${res.status}`);
  return (await res.json()) as RegistryBuildsView;
}
